/**
 * The ORG Settings tab vocabulary — shared by the routed page (`OrgSettingsScreen`) and the modal
 * (`OrgSettingsModal`) so the two chromes can never disagree about which tabs exist, their order, or who is
 * allowed to see one. The `settingsTabs.ts` shape, for the org side.
 */
export type OrgSettingsTab = "organization" | "account" | "artistPlan" | "tutorials";

export interface OrgSettingsTabDef {
  key: OrgSettingsTab;
  label: string;
  /** Capability required to see the tab; undefined ⇒ every seat. */
  cap?: string;
}

/**
 * Organization first, then Account.
 *
 * **Organization gates on `roster:manage_seats`** — the same capability `PUT /orgs/{orgId}` carries, so the
 * tab is visible exactly when its Save can succeed (PO, 2026-09-01). Six of the nine seeded org seats do not
 * hold it, and for them the profile form used to render fully editable and 403 on Save.
 *
 * **Account has no gate, and that is why the dialog still opens for a staffer**: the roster shell has no
 * Settings gear, so this is an org seat's only door to their own password. A staffer therefore lands on a
 * one-tab dialog — which is why the account chip's entry is LABELLED for the half the seat gets.
 */
export const ORG_SETTINGS_TABS: readonly OrgSettingsTabDef[] = [
  { key: "organization", label: "Organization", cap: "roster:manage_seats" },
  { key: "account", label: "Account" },
  { key: "artistPlan", label: "Artist plan" },
  // ART-293: the admin console's how-to videos for this org's type. Shown only when the list has something in it.
  { key: "tutorials", label: "Tutorials" },
];

/**
 * The tabs a seat may see, in order. Both chromes filter through this so neither can drift.
 *
 * **Artist plan is doubly conditional, and neither condition is a capability** (ART-142). It needs an ARTIST
 * in focus — the action targets one band, and this dialog opens from a shell that may have none — and it
 * needs the server's own `canManagePlan` verdict for that artist, which is
 * `(root ∈ {artist, management}) AND money access`. So a label or booking seat, and a management intern,
 * never see the tab at all: a tab whose only outcome is a refusal is the dead button this codebase refuses
 * elsewhere.
 *
 * **Tutorials** (ART-293) shows only when this org type's tutorials list has rows — `hasTutorials`.
 *
 * The verdict is PASSED IN rather than derived here, because it is served on the plan read and re-deriving it
 * on the client would be a second implementation of a permission rule.
 */
export function visibleOrgSettingsTabs(
  canManageOrg: boolean,
  canManageArtistPlan = false,
  hasTutorials = false,
): readonly OrgSettingsTabDef[] {
  return ORG_SETTINGS_TABS.filter((t) => {
    if (t.key === "artistPlan") return canManageArtistPlan;
    if (t.key === "tutorials") return hasTutorials;
    return t.cap !== "roster:manage_seats" || canManageOrg;
  });
}
